import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import ParticleCanvas from "./ParticleCanvas";
import ClubLights from "./ClubLights";
import MagneticButton from "./MagneticButton";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const ySky = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const yDune = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const blobScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);

  // Scroll-linked letter drift — each letter of "SUNBURN" translates differently
  const l1 = useTransform(scrollYProgress, [0, 1], ["0vw", "-18vw"]);
  const l2 = useTransform(scrollYProgress, [0, 1], ["0vw", "-9vw"]);
  const l3 = useTransform(scrollYProgress, [0, 1], ["0vw", "-4vw"]);
  const l4 = useTransform(scrollYProgress, [0, 1], ["0vw", "2vw"]);
  const l5 = useTransform(scrollYProgress, [0, 1], ["0vw", "7vw"]);
  const l6 = useTransform(scrollYProgress, [0, 1], ["0vw", "12vw"]);
  const l7 = useTransform(scrollYProgress, [0, 1], ["0vw", "18vw"]);
  const letterYs = [l1, l2, l3, l4, l5, l6, l7];
  const letters = ["S", "U", "N", "B", "U", "R", "N"];

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative hero-h w-full overflow-hidden bg-[#0C0A09] scanlines"
    >
      {/* SVG filters shared for heat-haze distortion */}
      <svg className="absolute inset-0 w-0 h-0 pointer-events-none" aria-hidden>
        <defs>
          {/* Static turbulence — no animate child, browser can cache the rasterisation */}
          <filter id="heat-haze" x="-8%" y="-8%" width="116%" height="116%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.013 0.029"
              numOctaves="2"
              seed="7"
              result="turb"
            />
            <feDisplacementMap in="SourceGraphic" in2="turb" scale="22" />
          </filter>
        </defs>
      </svg>

      {/* Layer 1: aurora gradient with parallax */}
      <motion.div
        style={{ y: ySky }}
        className="absolute inset-0 -top-[10%] -bottom-[10%]"
        aria-hidden
      >
        <div className="absolute inset-0 aurora aurora-slow" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0c0a09]/30 to-[#0c0a09]" />
      </motion.div>

      {/* Layer 2: HEAT-HAZED SUN — the large sunset orb distorted by SVG turbulence */}
      <motion.div
        style={{ scale: blobScale, y: ySky, filter: "url(#heat-haze)" }}
        className="absolute left-1/2 top-[58%] -translate-x-1/2 -translate-y-1/2 w-[140vmin] h-[140vmin] pointer-events-none"
        aria-hidden
      >
        <svg viewBox="0 0 600 600" className="w-full h-full opacity-90">
          <defs>
            <radialGradient id="sun" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#FDBA74" stopOpacity="0.95" />
              <stop offset="40%" stopColor="#EA580C" stopOpacity="0.7" />
              <stop offset="70%" stopColor="#9D174D" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#0C0A09" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="300" cy="300" r="240" fill="url(#sun)" />
          {/* Thin sun bands — rising above the dune */}
          <g opacity="0.55">
            {[260, 250, 240, 230].map((y, i) => (
              <rect
                key={i}
                x="60"
                y={y}
                width="480"
                height="1.5"
                fill="#FDBA74"
                opacity={0.4 - i * 0.08}
              />
            ))}
          </g>
        </svg>
      </motion.div>

      {/* Layer 3: particle canvas */}
      <div className="absolute inset-0 pointer-events-none">
        <ParticleCanvas />
      </div>

      {/* Layer 3.5: Club lights (beams, lasers, strobe, crowd silhouette) */}
      <ClubLights />

      {/* Layer 4: dune silhouettes parallax */}
      <motion.div
        style={{ y: yDune }}
        className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none"
        aria-hidden
      >
        <svg
          viewBox="0 0 1440 600"
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
        >
          <path
            d="M0,420 C240,320 420,460 720,380 C1020,300 1240,460 1440,380 L1440,600 L0,600 Z"
            fill="#1c1917"
            opacity="0.92"
          />
          <path
            d="M0,500 C260,440 520,540 820,480 C1120,420 1240,520 1440,480 L1440,600 L0,600 Z"
            fill="#0C0A09"
          />
        </svg>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-44 pb-16 lg:pb-24 hero-h flex flex-col justify-end"
      >
        {/* Letter-drift SUNBURN with chromatic aberration */}
        <h1
          data-testid="hero-title"
          className="font-display font-black uppercase text-[#FFF7ED] leading-[0.86] tracking-[-0.04em] text-[clamp(3rem,11vw,11rem)] chromatic"
        >
          <span className="flex flex-wrap">
            {letters.map((ch, i) => (
              <motion.span
                key={i}
                style={{ x: letterYs[i] }}
                className="inline-block will-change-transform"
              >
                {ch}
              </motion.span>
            ))}
          </span>
          <span className="block mask-reveal">
            <motion.span
              className="inline-block font-serif-italic text-stroke font-normal"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ delay: 0.35, duration: 1, ease: [0.77, 0, 0.175, 1] }}
            >
              desert mirage
            </motion.span>
          </span>
        </h1>

        <div className="mt-8 lg:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="lg:col-span-5 font-body text-sm lg:text-base text-stone-300 leading-relaxed max-w-md"
          >
            A three-night open-air techno ritual at{" "}
            <span className="text-[#FFF7ED]">
              Embassy International Riding School
            </span>
            . One stage, one horizon, pure low-end.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="lg:col-span-4 lg:col-start-9 flex flex-wrap items-center gap-4"
          >
            <MagneticButton
              as="a"
              href="#passes"
              data-testid="hero-tickets-cta"
              className="group inline-flex items-center gap-3 bg-[#EA580C] text-white px-7 py-4 text-xs font-bold uppercase tracking-[0.28em] hover:bg-[#F59E0B] hover:text-[#0C0A09] transition-colors duration-300"
            >
              Get Tickets
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12h14M13 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="square"
                />
              </svg>
            </MagneticButton>
            <a
              href="#lineup"
              data-testid="hero-lineup-cta"
              className="inline-flex items-center gap-2 text-[#FFF7ED] px-4 py-4 text-xs font-semibold uppercase tracking-[0.28em] border-b border-[#FFF7ED]/30 hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors"
            >
              See the lineup
            </a>
          </motion.div>
        </div>

        {/* Tech readout strip — DJ-equipment style */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 lg:mt-16 border-t border-[#292524] pt-4 flex flex-wrap items-center gap-x-6 gap-y-2 readout text-[10px] tracking-[0.24em] uppercase text-stone-500"
          data-testid="hero-readout"
        >
          <span className="inline-flex items-center gap-2">
            <span className="eq" aria-hidden>
              <span /><span /><span /><span /><span />
            </span>
            <span className="text-[#22D3EE]">CH.01 OUT</span>
          </span>
          <span>
            <span className="text-stone-600">LAT</span> 13.1986° N
          </span>
          <span>
            <span className="text-stone-600">LON</span> 77.7066° E
          </span>
          <span>
            <span className="text-stone-600">BPM</span>{" "}
            <span className="text-[#F59E0B]">128–136</span>
          </span>
          <span>
            <span className="text-stone-600">CAP</span> 5,000
          </span>
          <span className="ml-auto text-[#22D3EE]">/// REC</span>
        </motion.div>
      </motion.div>
    </section>
  );
}
